import React, { useState, useEffect } from 'react';
import './index.css';
import { Amplify } from 'aws-amplify';
import { signIn, signUp, signOut, getCurrentUser, fetchAuthSession, confirmSignUp, resetPassword, confirmResetPassword, resendSignUpCode } from 'aws-amplify/auth';
import awsConfig from './aws-config';
import clsx from 'clsx';
import { Home } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Configure Amplify
Amplify.configure(awsConfig);

// 이미지 import
import dailyIcon from './assets/images/daily.png';
import articleIcon from './assets/images/article.png';
import compIcon from './assets/images/comp.png';
import govIcon from './assets/images/gov.png';
import enIcon from './assets/images/en.png';
import jaIcon from './assets/images/ja.png';
import quickIcon from './assets/images/quick.png';
import deepIcon from './assets/images/deep.png';
import titleIcon from './assets/images/title.png';
import creativeIcon from './assets/images/creative.png';
import bizIcon from './assets/images/biz.png';
import editIcon from './assets/images/edit.png';
import gptIcon from './assets/images/GPT.png';
import claudeIcon from './assets/images/Claude.png';
import perplexityIcon from './assets/images/Perplexity.png';

// Nexus Card Styles
const nexusCardStyles = `
  .nexus-card {
    position: relative;
    background: #FFFFFF;
    border: 1px solid #E5E5E5;
    border-radius: 12px;
    padding: 24px;
    transition: all 0.3s ease;
    min-height: 140px;
  }

  .nexus-card:hover {
    border-color: #0066FF;
    box-shadow: 0 4px 12px rgba(0, 102, 255, 0.1);
    transform: translateY(-2px);
  }

  .nexus-card .card-title {
    font-size: 18px;
    font-weight: 700;
    color: #1A1A1A;
    line-height: 1.3;
    margin-bottom: 8px;
  }

  .nexus-card .card-description {
    font-size: 13px;
    color: #666666;
    line-height: 1.5;
  }

  .nexus-card .card-icon {
    width: 48px;
    height: 48px;
    background: #F0F5FF;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0066FF;
    font-size: 24px;
  }

  .nexus-card .card-icon img {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = nexusCardStyles;
  document.head.appendChild(styleSheet);
}

function App() {
  const [currentPage, setCurrentPage] = useState('portal'); // 'portal', 'login', 'signup', 'verify', 'forgotPassword', 'resetPassword'
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [tempEmail, setTempEmail] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Check if user is already logged in on component mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      if (user && session) {
        setIsLoggedIn(true);
        setUserEmail(user.signInDetails?.loginId || user.username || '');
      }
    } catch (error) {
      setIsLoggedIn(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { isSignedIn, nextStep } = await signIn({
        username: formData.email,
        password: formData.password,
      });

      if (isSignedIn) {
        setIsLoggedIn(true);
        setUserEmail(formData.email);
        setCurrentPage('portal');
        await checkAuthStatus();
      } else {
        console.log('Next step:', nextStep);
      }
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = '로그인에 실패했습니다.';

      if (error.name === 'UserNotFoundException') {
        errorMessage = '존재하지 않는 사용자입니다.';
      } else if (error.name === 'NotAuthorizedException') {
        errorMessage = '이메일 또는 비밀번호가 올바르지 않습니다.';
      } else if (error.name === 'UserNotConfirmedException') {
        errorMessage = '이메일 인증이 필요합니다.';
        setTempEmail(formData.email);
        setCurrentPage('verify');
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      setError('비밀번호는 8자 이상이며, 대소문자, 숫자, 특수문자(~ # 제외)를 포함해야 합니다.');
      return;
    }

    setIsLoading(true);

    try {
      const emailName = formData.email.split('@')[0];
      await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
            name: emailName,
          },
        }
      });

      setTempEmail(formData.email);
      setCurrentPage('verify');
      setSuccessMessage('이메일로 인증 코드가 발송되었습니다. 인증 코드를 입력해주세요.');
    } catch (error) {
      console.error('Signup error:', error);
      let errorMessage = '회원가입에 실패했습니다.';

      if (error.name === 'UsernameExistsException') {
        errorMessage = '이미 존재하는 이메일입니다.';
      } else if (error.name === 'InvalidPasswordException') {
        errorMessage = '비밀번호는 8자 이상이며, 대소문자, 숫자, 특수문자를 포함해야 합니다.';
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setError('');

    if (!verificationCode || verificationCode.length !== 6) {
      setError('6자리 인증 코드를 입력해주세요.');
      return;
    }

    setIsLoading(true);

    try {
      await confirmSignUp({
        username: tempEmail,
        confirmationCode: verificationCode
      });

      setSuccessMessage('이메일 인증이 완료되었습니다!');

      // 자동 로그인 시도
      const loginResult = await signIn({
        username: tempEmail,
        password: formData.password,
      });

      if (loginResult.isSignedIn) {
        setIsLoggedIn(true);
        setUserEmail(tempEmail);
        setCurrentPage('portal');
      } else {
        setTimeout(() => {
          setCurrentPage('login');
        }, 2000);
      }
    } catch (error) {
      console.error('Verification error:', error);
      let errorMessage = '인증에 실패했습니다.';

      if (error.name === 'CodeMismatchException') {
        errorMessage = '인증 코드가 올바르지 않습니다.';
      } else if (error.name === 'ExpiredCodeException') {
        errorMessage = '인증 코드가 만료되었습니다. 코드를 재전송해주세요.';
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      await resendSignUpCode({ username: tempEmail });
      setSuccessMessage('인증 코드가 재발송되었습니다.');
    } catch (error) {
      console.error('Resend code error:', error);
      setError('인증 코드 재발송에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await resetPassword({ username: formData.email });
      setTempEmail(formData.email);
      setCurrentPage('resetPassword');
      setSuccessMessage('비밀번호 재설정 코드가 이메일로 발송되었습니다.');
    } catch (error) {
      console.error('Forgot password error:', error);
      let errorMessage = '비밀번호 재설정 요청에 실패했습니다.';

      if (error.name === 'UserNotFoundException') {
        errorMessage = '존재하지 않는 사용자입니다.';
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmNewPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    setIsLoading(true);

    try {
      await confirmResetPassword({
        username: tempEmail,
        confirmationCode: resetCode,
        newPassword: newPassword
      });

      setSuccessMessage('비밀번호가 성공적으로 재설정되었습니다!');
      setTimeout(() => {
        setCurrentPage('login');
      }, 2000);
    } catch (error) {
      console.error('Reset password error:', error);
      let errorMessage = '비밀번호 재설정에 실패했습니다.';

      if (error.name === 'CodeMismatchException') {
        errorMessage = '인증 코드가 올바르지 않습니다.';
      } else if (error.name === 'InvalidPasswordException') {
        errorMessage = '비밀번호는 8자 이상이며, 대소문자, 숫자, 특수문자를 포함해야 합니다.';
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      setIsLoggedIn(false);
      setUserEmail('');

      // SSO 쿠키 삭제
      document.cookie = 'sso_id_token=; domain=.sedaily.ai; path=/; max-age=0';
      document.cookie = 'sso_access_token=; domain=.sedaily.ai; path=/; max-age=0';
      localStorage.removeItem('cognito_id_token');
      localStorage.removeItem('cognito_access_token');
      localStorage.removeItem('cognito_token_expiry');
    } catch (error) {
      console.error('Logout error:', error);

      // 에러 발생 시에도 쿠키 삭제
      document.cookie = 'sso_id_token=; domain=.sedaily.ai; path=/; max-age=0';
      document.cookie = 'sso_access_token=; domain=.sedaily.ai; path=/; max-age=0';
      localStorage.removeItem('cognito_id_token');
      localStorage.removeItem('cognito_access_token');
      localStorage.removeItem('cognito_token_expiry');
    }
  };

  const handleServiceClick = async (service) => {
    if (service.status !== 'active') return;

    // Set currentModel in localStorage for m1.sedaily.ai range AI services
    if (service.name === 'ChatGPT') {
      localStorage.setItem('currentModel', 'gpt');
    } else if (service.name === 'Claude') {
      localStorage.setItem('currentModel', 'claude');
    } else if (service.name === 'Perplexity') {
      localStorage.setItem('currentModel', 'perplexity');
    }

    try {
      const session = await fetchAuthSession();

      if (session && session.tokens) {
        const idToken = session.tokens.idToken?.toString();
        const accessToken = session.tokens.accessToken?.toString();

        if (idToken && accessToken) {
          localStorage.setItem('cognito_id_token', idToken);
          localStorage.setItem('cognito_access_token', accessToken);
          localStorage.setItem('cognito_token_expiry', new Date().getTime() + 3600000);

          document.cookie = `sso_id_token=${idToken}; domain=.sedaily.ai; path=/; max-age=3600; secure; SameSite=Lax`;
          document.cookie = `sso_access_token=${accessToken}; domain=.sedaily.ai; path=/; max-age=3600; secure; SameSite=Lax`;

          window.open(service.url, '_blank');
        } else {
          window.open(service.url, '_blank');
        }
      } else {
        window.open(service.url, '_blank');
      }
    } catch (error) {
      console.error('Error accessing service:', error);
      window.open(service.url, '_blank');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  // 로그인 페이지
  if (currentPage === 'login') {
    return <LoginPage
      formData={formData}
      isLoading={isLoading}
      error={error}
      onSubmit={handleLogin}
      onInputChange={handleInputChange}
      onGoToSignup={() => setCurrentPage('signup')}
      onForgotPassword={() => setCurrentPage('forgotPassword')}
      onBackToPortal={() => setCurrentPage('portal')}
    />;
  }

  // 회원가입 페이지
  if (currentPage === 'signup') {
    return <SignupPage
      formData={formData}
      isLoading={isLoading}
      error={error}
      onSubmit={handleSignup}
      onInputChange={handleInputChange}
      onBackToLogin={() => setCurrentPage('login')}
    />;
  }

  // 이메일 인증 페이지
  if (currentPage === 'verify') {
    return <VerificationPage
      verificationCode={verificationCode}
      isLoading={isLoading}
      error={error}
      successMessage={successMessage}
      email={tempEmail}
      onSubmit={handleVerification}
      onCodeChange={setVerificationCode}
      onResendCode={handleResendCode}
      onBackToLogin={() => setCurrentPage('login')}
    />;
  }

  // 비밀번호 찾기 페이지
  if (currentPage === 'forgotPassword') {
    return <ForgotPasswordPage
      formData={formData}
      isLoading={isLoading}
      error={error}
      onSubmit={handleForgotPassword}
      onInputChange={handleInputChange}
      onBackToLogin={() => setCurrentPage('login')}
    />;
  }

  // 비밀번호 재설정 페이지
  if (currentPage === 'resetPassword') {
    return <ResetPasswordPage
      resetCode={resetCode}
      newPassword={newPassword}
      confirmNewPassword={confirmNewPassword}
      isLoading={isLoading}
      error={error}
      successMessage={successMessage}
      email={tempEmail}
      onSubmit={handleResetPassword}
      onCodeChange={setResetCode}
      onNewPasswordChange={setNewPassword}
      onConfirmNewPasswordChange={setConfirmNewPassword}
      onBackToLogin={() => setCurrentPage('login')}
    />;
  }

  // 메인 포털 페이지
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="text-2xl font-bold text-gray-900">
                🚀 AI NOVA
              </div>
              <div className="ml-4 text-sm text-gray-500">
                News Orchestrating Vivid Agent
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <a
                href="https://sedaily.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-500 hover:text-blue-600 transition-colors cursor-pointer"
              >
                sedaily.ai
              </a>

              {isLoggedIn ? (
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-700">{userEmail}</span>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    로그아웃
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setCurrentPage('login')}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  로그인
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            기자를 위한 AI NOVA
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            News Orchestrating Vivid Agent<br/>
            취재는 10배 깊게, 작성은 100배 빠르게, 불가능은 가능하게.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-7xl mx-auto">
          {[
            {
              name: '일보 버디',
              description: '단순한 아이디어도 꼼꼼한 일보로 작성',
              url: 'https://b1.sedaily.ai/11',
              icon: dailyIcon,
              status: 'active',
              category: '기획/작성',
              features: ['한 단어만 입력해도 3개 기사 방향 제안']
            },
            {
              name: '기사 버디',
              description: '일보와 아이디어만 넣으면 기사 초안 작성',
              url: 'https://b1.sedaily.ai/22',
              icon: articleIcon,
              status: 'active',
              category: '기획/작성',
              features: ['[대괄호]로 확인 필요 사항 투명하게 표시']
            },
            {
              name: '보도자료_기업',
              description: '기업 보도자료로 기사 초안 작성',
              url: 'https://w1.sedaily.ai/11',
              icon: compIcon,
              status: 'active',
              category: '기사 작성',
              features: ['신제품, 서비스 핵심 요약', '분기 실적 분석']
            },
            {
              name: '보도자료_공공',
              description: '정부, 공공기관 보도자료로 기사 초안 작성',
              url: 'https://w1.sedaily.ai/22',
              icon: govIcon,
              status: 'active',
              category: '기사 작성',
              features: ['경제정책 영향 분석', '부동산 금융 규제 요약']
            },
            {
              name: '외신_영어',
              description: '영문 외신 기사로 한글 기사 초안 작성',
              url: 'https://f1.sedaily.ai/11',
              icon: enIcon,
              status: 'active',
              category: '번역',
              features: ['실시간 영문 뉴스 번역', '한국 상황 맥락 추가']
            },
            {
              name: '외신_일어',
              description: '일본어 외신 뉴스로 한글 기사 초안 작성',
              url: 'https://f1.sedaily.ai/22',
              icon: jaIcon,
              status: 'active',
              category: '번역',
              features: ['일본식 표현 순화', '정치 경제 용어 표준화']
            },
            {
              name: '퇴고_단문 (1,000자 미만)',
              description: '문맥의 첫 2초 임팩트를 이탈률 50% 감소',
              url: 'https://r1.sedaily.ai/11',
              icon: quickIcon,
              status: 'active',
              category: '퇴고/편집',
              features: ['3가지 개선 방향 제시', '번호로 즉시 선택']
            },
            {
              name: '퇴고_장문 (1,000자 이상)',
              description: '독자 이탈 포인트 예측, 구조 재배치 제안',
              url: 'https://r1.sedaily.ai/22',
              icon: deepIcon,
              status: 'active',
              category: '퇴고/편집',
              features: ['영웅서사/미스터리/플래시백 구조 선택']
            },
            {
              name: '제목 생성_5종',
              description: '클릭우드형 등 5가지 유형별 차별화',
              url: 'https://t1.sedaily.ai/11',
              icon: titleIcon,
              status: 'active',
              category: '제목 생성',
              features: ['저널리즘 충실형', '균형잡힌 후킹형 등']
            },
            {
              name: '제목 창의_7종',
              description: '언어 혁신, 충돌 생성, 감성 증폭 엔진',
              url: 'https://t1.sedaily.ai/22',
              icon: creativeIcon,
              status: 'active',
              category: '제목 생성',
              features: ['7가지 엔진으로 새로운 아이디어 제시']
            },
            {
              name: '교열_경제분야',
              description: '숫자, 단위 표기 등 맞춤법 완벽 체크',
              url: 'https://p1.sedaily.ai/11',
              icon: bizIcon,
              status: 'active',
              category: '교열',
              features: ['주어-서술어 호응 검증', '중복 표현 제거']
            },
            {
              name: '교열_사회분야',
              description: '인용 부호 정확성, 간접 인용 문법 체크',
              url: 'https://p1.sedaily.ai/22',
              icon: editIcon,
              status: 'active',
              category: '교열',
              features: ['불필요한 피동문 개선', '문단 간 연결성 검토']
            },
            {
              name: 'ChatGPT',
              description: '일상 업무의 만능 AI - 무엇이든 물어보세요',
              url: 'https://m1.sedaily.ai/login?redirect=%2Fg&model=gpt',
              icon: gptIcon,
              status: 'active',
              category: '범용 AI',
              features: ['일상 업무 지원', '창작 도움'],
              iconBg: '#10A37F'
            },
            {
              name: 'Claude',
              description: '논리적 글쓰기 전문가 - 깊이 있는 분석',
              url: 'https://m1.sedaily.ai/login?redirect=%2Fc&model=claude',
              icon: claudeIcon,
              status: 'active',
              category: '범용 AI',
              features: ['논리적 분석', '전문 글쓰기'],
              iconBg: '#D97706'
            },
            {
              name: 'Perplexity',
              description: '실시간 검색 전문가 - 최신 정보 제공',
              url: 'https://m1.sedaily.ai/login?redirect=%2Fp&model=perplexity',
              icon: perplexityIcon,
              status: 'active',
              category: '범용 AI',
              features: ['실시간 검색', '최신 정보'],
              iconBg: '#0066FF'
            }
          ].map((service, index) => (
            <div
              key={index}
              onClick={() => handleServiceClick(service)}
              className={`
                nexus-card
                ${service.status === 'active' ? 'cursor-pointer' : 'opacity-75 cursor-pointer'}
              `}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div
                    className="card-icon"
                    style={service.iconBg ? { backgroundColor: service.iconBg } : {}}
                  >
                    <img src={service.icon} alt={service.name} />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="card-title">
                    {service.name}
                  </h3>
                  <p className="card-description">
                    {service.description}
                    {service.features && (
                      <><br/>{service.features.slice(0, 2).join(', ')}</>
                    )}
                  </p>
                </div>
              </div>
              {service.status === 'active' && (
                <div className="absolute bottom-4 right-4 text-blue-500 opacity-70">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <div className="bg-white rounded-lg shadow-sm p-6 max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              서비스 이용 안내
            </h3>
            <p className="text-gray-600 text-sm">
              각 AI 서비스는 독립적으로 운영되며, 서비스별로 별도의 로그인이 필요할 수 있습니다.<br/>
              <strong>AI 버디</strong>는 24시간 언제든 이용 가능하며, 새벽에도 주말에도 마감 직전에도 함께합니다.<br/>
              문의사항은 <a href="mailto:ai@sedaily.com" className="text-blue-600 hover:underline">ai@sedaily.com</a>으로 연락주세요.
            </p>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-500 text-sm">
            <p>© 2024 서울경제신문. All rights reserved.</p>
            <p className="mt-1">Powered by AWS & AI Technology</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// =========================
// 서브 컴포넌트들
// =========================

// 로그인 페이지
const LoginPage = ({ formData, isLoading, error, onSubmit, onInputChange, onGoToSignup, onForgotPassword, onBackToPortal }) => (
  <div
    className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
    style={{
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
    }}
  >
    {/* 배경 - 저널리즘의 예술 */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-0 left-0 w-[550px] h-[550px] opacity-[0.12]">
        <img
          src="/images/illustrations/pen1.png"
          alt=""
          className="w-full h-full object-contain"
          style={{
            transform: 'rotate(-15deg) translate(10%, 10%)',
            filter: 'grayscale(100%) contrast(1.15) brightness(0.95)'
          }}
        />
      </div>
      <div className="absolute top-0 right-0 w-[550px] h-[550px] opacity-[0.12]">
        <img
          src="/images/illustrations/pen2.png"
          alt=""
          className="w-full h-full object-contain"
          style={{
            transform: 'rotate(15deg) translate(-10%, 10%)',
            filter: 'grayscale(100%) contrast(1.15) brightness(0.95)'
          }}
        />
      </div>
      <div className="absolute bottom-0 left-0 w-[550px] h-[550px] opacity-[0.12]">
        <img
          src="/images/illustrations/pen3.png"
          alt=""
          className="w-full h-full object-contain"
          style={{
            transform: 'rotate(15deg) translate(10%, -10%)',
            filter: 'grayscale(100%) contrast(1.15) brightness(0.95)'
          }}
        />
      </div>
      <div className="absolute bottom-0 right-0 w-[550px] h-[550px] opacity-[0.12]">
        <img
          src="/images/illustrations/pen4.png"
          alt=""
          className="w-full h-full object-contain"
          style={{
            transform: 'rotate(-15deg) translate(-10%, -10%)',
            filter: 'grayscale(100%) contrast(1.15) brightness(0.95)'
          }}
        />
      </div>

      {/* 우아한 그라데이션 */}
      <div
        className="absolute top-1/4 right-1/4 w-[750px] h-[750px]"
        style={{
          background: 'radial-gradient(circle, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 40%, rgba(90, 103, 216, 0.04) 60%, transparent 80%)',
          filter: 'blur(100px)'
        }}
      />
      <div
        className="absolute bottom-1/4 left-1/4 w-[700px] h-[700px]"
        style={{
          background: 'radial-gradient(circle, rgba(79, 172, 254, 0.10) 0%, rgba(0, 242, 254, 0.06) 40%, rgba(102, 126, 234, 0.03) 60%, transparent 80%)',
          filter: 'blur(100px)'
        }}
      />
    </div>

    {/* 홈 버튼 */}
    <button
      onClick={onBackToPortal}
      className="absolute top-6 sm:top-8 left-6 sm:left-8 flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl transition-all hover:scale-105 z-10 group"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        color: "#4a5568",
        border: "1px solid rgba(226, 232, 240, 0.8)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
      }}
    >
      <Home size={18} className="transition-transform group-hover:-translate-x-0.5" />
      <span className="font-medium text-sm hidden sm:inline">홈으로</span>
    </button>

    {/* 로그인 카드 */}
    <div className="max-w-md w-full z-10">
      <div
        className="rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(25px)",
          border: "1px solid rgba(255, 255, 255, 0.9)",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(102, 126, 234, 0.05), 0 0 1px rgba(0, 0, 0, 0.1)"
        }}
      >
        <div className="p-8 sm:p-10">
          {/* 로고 섹션 */}
          <div className="flex flex-col items-center mb-10">
            <div className="relative group">
              <div
                className="absolute inset-0 rounded-full blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  transform: 'scale(1.5)'
                }}
              />
              <img
                src="/images/ainova.png"
                alt="AI NOVA Logo"
                className="w-56 h-56 object-contain relative z-10 transform transition-all duration-300 group-hover:scale-105"
              />
            </div>
          </div>

          <form className="space-y-5" onSubmit={onSubmit}>
            <div className="space-y-4">
              <InputField
                id="email"
                label="이메일"
                type="email"
                value={formData.email}
                onChange={(e) => onInputChange("email", e.target.value)}
                autoComplete="username"
                placeholder="이메일 주소를 입력하세요"
              />

              <InputField
                id="password"
                label="비밀번호"
                type="password"
                value={formData.password}
                onChange={(e) => onInputChange("password", e.target.value)}
                autoComplete="current-password"
                placeholder="비밀번호를 입력하세요"
              />
            </div>

            {error && <ErrorMessage message={error} />}

            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                비밀번호 찾기
              </button>
            </div>

            <SubmitButton isLoading={isLoading} text={isLoading ? "로그인 중..." : "로그인"} />

            {/* 구분선 */}
            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" style={{ borderColor: "#e2e8f0" }}></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="px-3" style={{ backgroundColor: "rgba(255, 255, 255, 0.95)", color: "#a0aec0" }}>
                  또는
                </span>
              </div>
            </div>

            {/* 회원가입 */}
            <div className="text-center">
              <span className="text-sm" style={{ color: "#718096" }}>
                아직 계정이 없으신가요?{" "}
              </span>
              <button
                type="button"
                className="text-sm font-semibold hover:underline transition-colors"
                style={{ color: "#667eea" }}
                onClick={onGoToSignup}
              >
                회원가입
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
);

// 회원가입 페이지
const SignupPage = ({ formData, isLoading, error, onSubmit, onInputChange, onBackToLogin }) => (
  <motion.div
    className="min-h-screen flex items-center relative overflow-hidden"
    style={{
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 50
    }}
    initial={{ x: '100%', filter: 'blur(20px)' }}
    animate={{ x: 0, filter: 'blur(0px)' }}
    exit={{ x: '100%', filter: 'blur(20px)' }}
    transition={{
      duration: 2.2,
      ease: [0.16, 1.0, 0.3, 1.0],
      filter: { duration: 1.8, ease: [0.22, 1.0, 0.36, 1.0] }
    }}
  >
    {/* 배경 - 우측에 대표 문양 */}
    <div className="absolute inset-0 pointer-events-none" style={{ overflow: 'visible' }}>
      <div
        className="absolute top-1/2 right-[5%] transform -translate-y-1/2 w-[900px] h-[900px] opacity-[0.12]"
        style={{
          maskImage: 'linear-gradient(to left, transparent 0%, black 15%, black 100%)',
          WebkitMaskImage: 'linear-gradient(to left, transparent 0%, black 15%, black 100%)'
        }}
      >
        <img
          src="/images/illustrations/pen4.png"
          alt=""
          className="w-full h-full object-contain"
          style={{
            transform: 'rotate(-8deg)',
            filter: 'grayscale(100%) contrast(1.15) brightness(0.92)'
          }}
        />
      </div>

      {/* 우아한 그라데이션 */}
      <div
        className="absolute top-1/4 right-1/4 w-[900px] h-[900px]"
        style={{
          background: 'radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.10) 40%, rgba(90, 103, 216, 0.05) 60%, transparent 80%)',
          filter: 'blur(120px)'
        }}
      />
    </div>

    {/* 홈 버튼 */}
    <button
      onClick={onBackToLogin}
      className="absolute top-6 sm:top-8 left-6 sm:left-8 flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl transition-all hover:scale-105 z-10 group"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        color: "#4a5568",
        border: "1px solid rgba(226, 232, 240, 0.8)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
      }}
    >
      <Home size={18} className="transition-transform group-hover:-translate-x-0.5" />
      <span className="font-medium text-sm hidden sm:inline">로그인으로</span>
    </button>

    {/* 회원가입 카드 */}
    <div className="w-full lg:w-1/2 px-6 sm:px-12 lg:px-20 py-20 sm:py-12 z-10">
      <div
        className="rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(25px)",
          border: "1px solid rgba(255, 255, 255, 0.9)",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(102, 126, 234, 0.05), 0 0 1px rgba(0, 0, 0, 0.1)"
        }}
      >
        <div className="p-8 sm:p-10">
          {/* 로고 섹션 */}
          <div className="flex flex-col items-center mb-8">
            <img
              src="/images/ainova.png"
              alt="AI NOVA Logo"
              className="w-48 h-48 object-contain transform transition-all duration-300 hover:scale-105"
            />
          </div>

          <form className="space-y-5" onSubmit={onSubmit}>
            <div className="space-y-4">
              <InputField
                id="email"
                label="이메일"
                type="email"
                value={formData.email}
                onChange={(e) => onInputChange("email", e.target.value)}
                autoComplete="email"
                placeholder="이메일 주소를 입력하세요"
              />

              <InputField
                id="password"
                label="비밀번호"
                type="password"
                value={formData.password}
                onChange={(e) => onInputChange("password", e.target.value)}
                autoComplete="new-password"
                placeholder="비밀번호 (8자 이상)"
              />
              <p className="mt-2 text-xs" style={{ color: "#718096" }}>
                * 대소문자, 숫자, 특수문자(~ # 제외)를 포함해야 합니다
              </p>

              <InputField
                id="confirmPassword"
                label="비밀번호 확인"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => onInputChange("confirmPassword", e.target.value)}
                autoComplete="new-password"
                placeholder="비밀번호 재입력"
              />
            </div>

            {error && <ErrorMessage message={error} />}

            <SubmitButton isLoading={isLoading} text={isLoading ? "처리중..." : "회원가입"} />

            {/* 구분선 */}
            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" style={{ borderColor: "#e2e8f0" }}></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="px-3" style={{ backgroundColor: "rgba(255, 255, 255, 0.98)", color: "#a0aec0" }}>
                  또는
                </span>
              </div>
            </div>

            {/* 로그인 링크 */}
            <div className="text-center">
              <span className="text-sm" style={{ color: "#718096" }}>
                이미 계정이 있으신가요?{" "}
              </span>
              <button
                type="button"
                onClick={onBackToLogin}
                className="text-sm font-semibold hover:underline transition-colors"
                style={{ color: "#667eea" }}
              >
                로그인
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </motion.div>
);

// 이메일 인증 페이지
const VerificationPage = ({ verificationCode, isLoading, error, successMessage, email, onSubmit, onCodeChange, onResendCode, onBackToLogin }) => (
  <motion.div
    className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
  >
    {/* 배경 */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-0 left-0 w-[550px] h-[550px] opacity-[0.12]">
        <img
          src="/images/illustrations/pen1.png"
          alt=""
          className="w-full h-full object-contain"
          style={{
            transform: 'rotate(-15deg) translate(10%, 10%)',
            filter: 'grayscale(100%) contrast(1.15) brightness(0.95)'
          }}
        />
      </div>
      <div className="absolute top-0 right-0 w-[550px] h-[550px] opacity-[0.12]">
        <img
          src="/images/illustrations/pen2.png"
          alt=""
          className="w-full h-full object-contain"
          style={{
            transform: 'rotate(15deg) translate(-10%, 10%)',
            filter: 'grayscale(100%) contrast(1.15) brightness(0.95)'
          }}
        />
      </div>
    </div>

    {/* 홈 버튼 */}
    <button
      onClick={onBackToLogin}
      className="absolute top-6 sm:top-8 left-6 sm:left-8 flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl transition-all hover:scale-105 z-10 group"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        color: "#4a5568",
        border: "1px solid rgba(226, 232, 240, 0.8)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
      }}
    >
      <Home size={18} className="transition-transform group-hover:-translate-x-0.5" />
      <span className="font-medium text-sm hidden sm:inline">홈으로</span>
    </button>

    {/* 인증 카드 */}
    <div className="max-w-md w-full z-10">
      <div
        className="rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(25px)",
          border: "1px solid rgba(255, 255, 255, 0.9)",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(102, 126, 234, 0.05), 0 0 1px rgba(0, 0, 0, 0.1)"
        }}
      >
        <div className="p-8 sm:p-10">
          {/* 로고 섹션 */}
          <div className="flex flex-col items-center mb-8">
            <img
              src="/images/ainova.png"
              alt="AI NOVA Logo"
              className="w-40 h-40 object-contain transform transition-all duration-300 hover:scale-105"
            />
            <h2 className="mt-6 text-2xl font-bold text-center" style={{ color: "#1a202c" }}>
              이메일 인증
            </h2>
            <p className="mt-2 text-center text-sm" style={{ color: "#718096" }}>
              {email}로 발송된<br />6자리 인증 코드를 입력해주세요
            </p>
          </div>

          <form className="space-y-5" onSubmit={onSubmit}>
            <div>
              <label htmlFor="code" className="block text-sm font-bold mb-2.5" style={{ color: "#1a202c" }}>
                인증 코드
              </label>
              <input
                id="code"
                name="code"
                type="text"
                maxLength="6"
                required
                value={verificationCode}
                onChange={(e) => onCodeChange(e.target.value.replace(/[^0-9]/g, ""))}
                className="appearance-none relative block w-full px-4 py-4 text-center text-2xl tracking-widest rounded-xl focus:outline-none transition-all duration-300"
                style={{
                  backgroundColor: "#ffffff",
                  border: "2px solid #e2e8f0",
                  color: "#2d3748",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.02)"
                }}
                placeholder="000000"
              />
            </div>

            {error && <ErrorMessage message={error} />}
            {successMessage && <SuccessMessage message={successMessage} />}

            <div className="space-y-3">
              <SubmitButton isLoading={isLoading} text={isLoading ? "인증 중..." : "인증하기"} />

              <button
                type="button"
                onClick={onResendCode}
                disabled={isLoading}
                className="w-full text-sm hover:underline"
                style={{ color: "#718096" }}
              >
                인증 코드 재발송
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </motion.div>
);

// 비밀번호 찾기 페이지
const ForgotPasswordPage = ({ formData, isLoading, error, onSubmit, onInputChange, onBackToLogin }) => (
  <div
    className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}
  >
    {/* 배경 */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-0 left-0 w-[550px] h-[550px] opacity-[0.12]">
        <img
          src="/images/illustrations/pen1.png"
          alt=""
          className="w-full h-full object-contain"
          style={{
            transform: 'rotate(-15deg) translate(10%, 10%)',
            filter: 'grayscale(100%) contrast(1.15) brightness(0.95)'
          }}
        />
      </div>
    </div>

    {/* 홈 버튼 */}
    <button
      onClick={onBackToLogin}
      className="absolute top-6 sm:top-8 left-6 sm:left-8 flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl transition-all hover:scale-105 z-10 group"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        color: "#4a5568",
        border: "1px solid rgba(226, 232, 240, 0.8)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
      }}
    >
      <Home size={18} className="transition-transform group-hover:-translate-x-0.5" />
      <span className="font-medium text-sm hidden sm:inline">로그인으로</span>
    </button>

    {/* 비밀번호 찾기 카드 */}
    <div className="max-w-md w-full z-10">
      <div
        className="rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(25px)",
          border: "1px solid rgba(255, 255, 255, 0.9)",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(102, 126, 234, 0.05), 0 0 1px rgba(0, 0, 0, 0.1)"
        }}
      >
        <div className="p-8 sm:p-10">
          <div className="flex flex-col items-center mb-8">
            <img
              src="/images/ainova.png"
              alt="AI NOVA Logo"
              className="w-40 h-40 object-contain transform transition-all duration-300 hover:scale-105"
            />
            <h2 className="mt-6 text-2xl font-bold text-center" style={{ color: "#1a202c" }}>
              비밀번호 찾기
            </h2>
            <p className="mt-2 text-center text-sm" style={{ color: "#718096" }}>
              가입하신 이메일로<br />비밀번호 재설정 코드를 보내드립니다
            </p>
          </div>

          <form className="space-y-5" onSubmit={onSubmit}>
            <InputField
              id="email"
              label="이메일"
              type="email"
              value={formData.email}
              onChange={(e) => onInputChange("email", e.target.value)}
              autoComplete="email"
              placeholder="이메일 주소를 입력하세요"
            />

            {error && <ErrorMessage message={error} />}

            <SubmitButton isLoading={isLoading} text={isLoading ? "코드 발송 중..." : "코드 발송"} />
          </form>
        </div>
      </div>
    </div>
  </div>
);

// 비밀번호 재설정 페이지
const ResetPasswordPage = ({ resetCode, newPassword, confirmNewPassword, isLoading, error, successMessage, email, onSubmit, onCodeChange, onNewPasswordChange, onConfirmNewPasswordChange, onBackToLogin }) => (
  <div
    className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}
  >
    {/* 배경 */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-0 left-0 w-[550px] h-[550px] opacity-[0.12]">
        <img
          src="/images/illustrations/pen1.png"
          alt=""
          className="w-full h-full object-contain"
          style={{
            transform: 'rotate(-15deg) translate(10%, 10%)',
            filter: 'grayscale(100%) contrast(1.15) brightness(0.95)'
          }}
        />
      </div>
    </div>

    {/* 홈 버튼 */}
    <button
      onClick={onBackToLogin}
      className="absolute top-6 sm:top-8 left-6 sm:left-8 flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl transition-all hover:scale-105 z-10 group"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        color: "#4a5568",
        border: "1px solid rgba(226, 232, 240, 0.8)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
      }}
    >
      <Home size={18} className="transition-transform group-hover:-translate-x-0.5" />
      <span className="font-medium text-sm hidden sm:inline">로그인으로</span>
    </button>

    {/* 비밀번호 재설정 카드 */}
    <div className="max-w-md w-full z-10">
      <div
        className="rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(25px)",
          border: "1px solid rgba(255, 255, 255, 0.9)",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(102, 126, 234, 0.05), 0 0 1px rgba(0, 0, 0, 0.1)"
        }}
      >
        <div className="p-8 sm:p-10">
          <div className="flex flex-col items-center mb-8">
            <img
              src="/images/ainova.png"
              alt="AI NOVA Logo"
              className="w-40 h-40 object-contain transform transition-all duration-300 hover:scale-105"
            />
            <h2 className="mt-6 text-2xl font-bold text-center" style={{ color: "#1a202c" }}>
              비밀번호 재설정
            </h2>
            <p className="mt-2 text-center text-sm" style={{ color: "#718096" }}>
              {email}로 발송된 코드와<br />새 비밀번호를 입력해주세요
            </p>
          </div>

          <form className="space-y-5" onSubmit={onSubmit}>
            <div>
              <label htmlFor="code" className="block text-sm font-bold mb-2.5" style={{ color: "#1a202c" }}>
                인증 코드
              </label>
              <input
                id="code"
                name="code"
                type="text"
                maxLength="6"
                required
                value={resetCode}
                onChange={(e) => onCodeChange(e.target.value.replace(/[^0-9]/g, ""))}
                className="appearance-none relative block w-full px-4 py-4 text-center text-2xl tracking-widest rounded-xl focus:outline-none transition-all duration-300"
                style={{
                  backgroundColor: "#ffffff",
                  border: "2px solid #e2e8f0",
                  color: "#2d3748",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.02)"
                }}
                placeholder="000000"
              />
            </div>

            <InputField
              id="newPassword"
              label="새 비밀번호"
              type="password"
              value={newPassword}
              onChange={(e) => onNewPasswordChange(e.target.value)}
              autoComplete="new-password"
              placeholder="새 비밀번호 (8자 이상)"
            />

            <InputField
              id="confirmNewPassword"
              label="새 비밀번호 확인"
              type="password"
              value={confirmNewPassword}
              onChange={(e) => onConfirmNewPasswordChange(e.target.value)}
              autoComplete="new-password"
              placeholder="새 비밀번호 재입력"
            />

            {error && <ErrorMessage message={error} />}
            {successMessage && <SuccessMessage message={successMessage} />}

            <SubmitButton isLoading={isLoading} text={isLoading ? "재설정 중..." : "비밀번호 재설정"} />
          </form>
        </div>
      </div>
    </div>
  </div>
);

// =========================
// 공통 UI 컴포넌트들
// =========================

const InputField = ({ id, label, type, value, onChange, autoComplete, placeholder }) => (
  <div>
    <label
      htmlFor={id}
      className="block text-sm font-bold mb-2.5"
      style={{ color: "#1a202c", letterSpacing: "-0.01em" }}
    >
      {label}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      autoComplete={autoComplete}
      required
      value={value}
      onChange={onChange}
      className="appearance-none relative block w-full px-4 py-4 rounded-xl focus:outline-none text-base transition-all duration-300"
      style={{
        backgroundColor: "#ffffff",
        border: "2px solid #e2e8f0",
        color: "#2d3748",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.02)"
      }}
      onFocus={(e) => {
        e.target.style.borderColor = "#667eea";
        e.target.style.boxShadow = "0 0 0 4px rgba(102, 126, 234, 0.12), 0 4px 12px rgba(102, 126, 234, 0.08)";
        e.target.style.backgroundColor = "#ffffff";
        e.target.style.transform = "translateY(-1px)";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = "#e2e8f0";
        e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.02)";
        e.target.style.backgroundColor = "#ffffff";
        e.target.style.transform = "translateY(0)";
      }}
      placeholder={placeholder}
    />
  </div>
);

const ErrorMessage = ({ message }) => (
  <div
    className="rounded-xl p-4 flex items-start gap-3"
    style={{
      backgroundColor: "#fff5f5",
      border: "1px solid #feb2b2"
    }}
  >
    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="#f56565" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
    <p className="text-sm flex-1" style={{ color: "#c53030" }}>
      {message}
    </p>
  </div>
);

const SuccessMessage = ({ message }) => (
  <div
    className="rounded-xl p-4 flex items-start gap-3"
    style={{
      backgroundColor: "#f0fdf4",
      border: "1px solid #86efac"
    }}
  >
    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="#22c55e" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
    <p className="text-sm flex-1" style={{ color: "#15803d" }}>
      {message}
    </p>
  </div>
);

const SubmitButton = ({ isLoading, text }) => (
  <button
    type="submit"
    disabled={isLoading}
    className={clsx(
      "group relative w-full flex justify-center items-center py-4 px-4 text-base font-bold rounded-xl focus:outline-none transition-all duration-300",
      isLoading
        ? "opacity-70 cursor-not-allowed"
        : "hover:shadow-2xl active:scale-[0.98]"
    )}
    style={{
      background: isLoading
        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        : "linear-gradient(135deg, #5a67d8 0%, #667eea 50%, #764ba2 100%)",
      color: "white",
      border: "none",
      boxShadow: isLoading
        ? "0 4px 14px rgba(102, 126, 234, 0.3)"
        : "0 6px 20px rgba(102, 126, 234, 0.45), 0 2px 8px rgba(118, 75, 162, 0.2)",
      backgroundSize: "200% 100%",
      backgroundPosition: "left center"
    }}
    onMouseEnter={(e) => {
      if (!isLoading) {
        e.target.style.transform = "translateY(-2px)";
        e.target.style.boxShadow = "0 12px 35px rgba(102, 126, 234, 0.55), 0 4px 16px rgba(118, 75, 162, 0.3)";
        e.target.style.backgroundPosition = "right center";
      }
    }}
    onMouseLeave={(e) => {
      if (!isLoading) {
        e.target.style.transform = "translateY(0)";
        e.target.style.boxShadow = "0 6px 20px rgba(102, 126, 234, 0.45), 0 2px 8px rgba(118, 75, 162, 0.2)";
        e.target.style.backgroundPosition = "left center";
      }
    }}
  >
    {isLoading && (
      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    )}
    {text}
  </button>
);

export default App;
